import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Send, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export default function InquiryReply() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/inquiry-reply/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This reply link is not valid."));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/inquiry-reply/${token}`, { body });
      setData((d) => ({ ...d, thread: r.data.thread }));
      setBody("");
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send your reply. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] text-stone-900" data-testid="inquiry-reply-page">
      <div className="max-w-2xl mx-auto px-6 py-14 sm:py-20">
        {error && !data ? (
          <div className="text-center" data-testid="inquiry-reply-error">
            <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-4">Reply link</div>
            <h1 className="font-serif text-3xl sm:text-4xl">{error}</h1>
          </div>
        ) : !data ? (
          <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500">Loading…</div>
        ) : (
          <>
            <header className="mb-12">
              {data.studio_logo && (
                <img src={data.studio_logo} alt="" className="h-10 w-auto mb-6 object-contain" />
              )}
              <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-3">Your conversation with</div>
              <h1 className="font-serif text-4xl sm:text-5xl leading-none" data-testid="inquiry-reply-studio">{data.studio_name}</h1>
              <p className="mt-4 text-sm text-stone-600">
                Hi {data.client_name?.split(" ")[0] || "there"} — write below and your message goes straight to the studio's inbox.
              </p>
            </header>

            <section className="space-y-4 mb-12" data-testid="inquiry-reply-thread">
              {data.thread.map((m, i) => (
                <div key={i} className={`max-w-[88%] ${m.direction === "client" ? "ml-auto" : ""}`}>
                  <div className={`px-5 py-4 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.direction === "client" ? "bg-stone-900 text-stone-50" : "bg-white border border-stone-200"
                  }`}>
                    {m.subject && <div className="text-[10px] uppercase tracking-[0.2em] opacity-60 mb-1.5">{m.subject}</div>}
                    {m.body}
                  </div>
                  <div className={`mt-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-400 ${m.direction === "client" ? "text-right" : ""}`}>
                    {m.direction === "client" ? "You" : data.studio_name} · {fmt(m.at)}
                  </div>
                </div>
              ))}
            </section>

            <form onSubmit={submit} className="space-y-4" data-testid="inquiry-reply-form">
              <textarea
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your reply…"
                className="w-full bg-white border border-stone-300 focus:border-stone-900 focus:outline-none px-5 py-4 text-sm leading-relaxed"
                data-testid="inquiry-reply-input"
              />
              {error && <div className="text-sm text-rose-600" data-testid="inquiry-reply-submit-error">{error}</div>}
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={sending || !body.trim()}
                  className="inline-flex items-center gap-2 whitespace-nowrap px-6 h-11 bg-stone-900 text-stone-50 text-[11px] uppercase tracking-[0.24em] disabled:opacity-40 hover:bg-stone-800 transition-colors"
                  data-testid="inquiry-reply-submit"
                >
                  <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send reply"}
                </button>
                {sent && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="inquiry-reply-sent">
                    <Check className="w-3.5 h-3.5" /> Sent — the studio has been notified
                  </span>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
